import MULTICALL3_ABI from 'abis/multicall3.json'
import { useMemo } from 'react'
import { Abi, Address, decodeFunctionResult, encodeFunctionData, Hex } from 'viem'
import { useReadContract } from 'wagmi'

import { RefetchPolicy } from '../config/RefetchPolicies'
import { MULTICALL_3_ADDRESSES } from '../constants/addresses'
import { useConnection } from './useConnection'

// ---- Local types (no external type imports) ----
type ContractCall = {
  address: Address
  abi: Abi
  functionName: string
  args?: readonly unknown[]
  chainId?: number
}

type UseReadContractsArgs = {
  contracts: readonly ContractCall[]
  query?: RefetchPolicy
  allowFailure?: boolean
}

type MulticallCall = {
  target: Address
  callData: Hex
}

type TryAggregateResultItem = {
  success: boolean
  returnData: Hex
}

type TryAggregateResult = TryAggregateResultItem[]

type ReadContractsItem<R = unknown, E = unknown> = {
  status: 'success' | 'failure'
  result: R
  error?: E
}

// We'll reuse the shape of useReadContract's return and just override `data`.
type UseReadContractsResult<R = unknown, E = unknown> = Omit<ReturnType<typeof useReadContract>, 'data'> & {
  data: ReadContractsItem<R, E>[] | undefined
}

// ---- Hook implementation ----

export function useReadContractMultiple<R = unknown, E = unknown>({
  contracts,
  query,
  allowFailure = true,
}: UseReadContractsArgs): UseReadContractsResult<R, E> {
  const { chainId } = useConnection()
  const multicall3Address =
    chainId != null ? (MULTICALL_3_ADDRESSES as Record<number, Address | undefined>)[chainId] : undefined

  // Determine enabled status outside of useMemo
  const contractsValid = contracts.length > 0 && contracts.every((c) => Boolean(c.abi && c.address && c.functionName))
  const enabled = contractsValid && !!multicall3Address && (query?.enabled ?? true)
  const requireSuccess = !allowFailure

  // 1. MEMOIZE THE ENTIRE CALL ARGUMENTS ARRAY
  const multicallArgs = useMemo(() => {
    // 1a. Build tryAggregate calls (target + callData)
    const calls: MulticallCall[] = contracts.map((c) => {
      // Use useMemo dependencies (contracts) to ensure this encoding only runs when needed
      const callData = encodeFunctionData({
        abi: c.abi,
        functionName: c.functionName,
        args: (c.args ?? []) as unknown[],
      })
      return {
        target: c.address,
        callData,
      }
    })
    // 1b. Return the stable arguments array [requireSuccess, calls]
    return [requireSuccess, calls] as const
  }, [contracts, requireSuccess]) // Dependencies: Only changes if input contracts or allowFailure changes

  // Single wagmi hook call to Multicall3.tryAggregate
  const readResult = useReadContract({
    address: multicall3Address,
    abi: MULTICALL3_ABI as Abi,
    functionName: 'tryAggregate',
    args: multicallArgs, // Pass the memoized array
    chainId,
    query: {
      ...(query || {}),
      enabled,
    },
  })

  const rawData = readResult.data as TryAggregateResult | undefined

  // Decode into `[ { status, result, error }, ... ]`
  const decodedData: ReadContractsItem[] | undefined = useMemo(() => {
    if (!rawData || !multicall3Address || contracts.length === 0) return undefined

    return rawData.map((item, i) => {
      if (!item.success) {
        return {
          status: 'failure',
          result: undefined,
          error: new Error(`Contract call ${i} reverted`),
        }
      }

      try {
        const contract = contracts[i]
        const decodedResult = decodeFunctionResult({
          abi: contract.abi,
          functionName: contract.functionName,
          data: item.returnData,
        })

        return {
          status: 'success',
          result: decodedResult,
          error: undefined,
        }
      } catch (e) {
        return {
          status: 'failure',
          result: undefined,
          error: e,
        }
      }
    })
  }, [rawData, multicall3Address, contracts]) // Decode only when rawData or contract configuration changes

  // Return same shape as useReadContract, but with our decoded `data`
  return {
    ...readResult,
    data: decodedData,
  }
}
